import { computed, Injectable, Signal } from '@angular/core';
import { User } from '@core/models/entities/user.models';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';
import { Parent } from '@core/models/entities/parent.models';
import { Role } from '@core/constants/roles.constants';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpApi } from './http.api';

@Injectable({ providedIn: 'root' })
export class ProfileApi {
  private _fetcher$: Observable<User> | null = null;

  private readonly _user$ = new BehaviorSubject<User | null>(null);

  private userSignal: Signal<User | null> = toSignal(this._user$, { initialValue: null });

  readonly userRole: Signal<Role | null> = computed(() => this.userSignal()?.role ?? null);

  constructor(private readonly httpApi: HttpApi) {}

  get user$(): Observable<User | null> {
    if (this.user) return this._user$.asObservable();
    if (this._fetcher$) return this._fetcher$;
    this._fetcher$ = new Observable<User>((observer) => this.fetch().subscribe(observer));
    return this._fetcher$;
  }

  get user(): User | null {
    return this._user$.value;
  }

  set user(user: User | null) {
    this._user$.next(user);
  }

  protected fetch(): Observable<User> {
    return this.httpApi
      .get<User>('user')
      .pipe(tap((user: User) => this._user$.next(user)))
      .pipe(finalize(() => (this._fetcher$ = null)));
  }

  refresh(): Observable<User> {
    return this.fetch();
  }

  patch(user: Partial<User>) {
    const currentUser = this.user || ({} as User);
    this._user$.next({ ...currentUser, ...user });
  }

  isIntakeFormCompleted(): boolean {
    const { role } = this.user || ({} as User);
    if (role !== Role.PARENT) return true;
    return !!(this.user as Parent).info;
  }

  deleteProfile(): Observable<boolean> {
    return this.httpApi.delete(`user`, { skipSuccessMessage: true });
  }
}
